import { TextInput, NumberInput, Stack, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { LevelCreate, type Level, type LevelCreate as LCT } from "@iso27001/shared";
import { ApiError } from "../../api/client.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";

interface Props {
    opened: boolean;
    onClose: () => void;
    initial: Level | null;
    api: {
        create: (b: LCT) => Promise<Level>;
        update: (id: number, b: Partial<LCT>) => Promise<Level>;
    };
    queryKey: readonly unknown[];
}

export function LevelFormModal({ opened, onClose, initial, api, queryKey }: Props) {
    const qc = useQueryClient();
    const isEdit = !!initial;
    const { t } = useTranslation();
    const apiError = useApiErrorMessage();
    // Levels have no closed state; only viewers see them read-only.
    const { readOnly } = useRecordAccess(false);
    const form = useForm({
        initialValues: {
            code: initial?.code ?? "",
            label: initial?.label ?? "",
            numericValue: initial?.numericValue ?? 1,
            sortOrder: initial?.sortOrder ?? 1,
        },
        // LevelCreate's `code` pattern carries no zod message of its own (see
        // packages/shared/src/levels.ts) — a validator message is user-facing
        // the moment it's wired through zodResolver, and packages/shared has
        // no locale to render it in. Supply the translated text here instead
        // of trusting zod's untranslated default ("Invalid") for whatever
        // check on `code` fails.
        validate: (values) => {
            const errors = zodResolver(LevelCreate)(values);
            if (errors.code) errors.code = t("levels.error.codePattern");
            return errors;
        },
    });
    const mut = useMutation({
        mutationFn: (vals: LCT) => (isEdit ? api.update(initial!.id, vals) : api.create(vals)),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey });
            notifications.show({ message: isEdit ? t("levels.form.notify.updated") : t("levels.form.notify.created"), color: "teal" });
            onClose();
        },
        onError: (e) => {
            if (e instanceof ApiError && e.code === "Conflict") {
                form.setFieldError("code", t("levels.error.codeConflict"));
            } else {
                notifications.show({ message: apiError(e, { fallback: "levels.error.saveFailed" }), color: "red" });
            }
        },
    });
    return (
        <EntityModal
            opened={opened}
            onClose={onClose}
            size="md"
            title={isEdit ? t("levels.form.title") : t("levels.newLevel")}
            code={initial?.code}
            readOnly={readOnly}
            onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
            saving={mut.isPending}
            submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
        >
            <Stack>
                <SimpleGrid cols={2}>
                    <TextInput label={t("common.field.code")} required readOnly={readOnly} {...form.getInputProps("code")} />
                    <TextInput label={t("levels.field.label")} required readOnly={readOnly} {...form.getInputProps("label")} />
                    <NumberInput label={t("levels.form.numericValue.label")} min={1} max={10} required readOnly={readOnly} {...form.getInputProps("numericValue")} />
                    <NumberInput label={t("levels.form.sortOrder.label")} min={0} required readOnly={readOnly} {...form.getInputProps("sortOrder")} />
                </SimpleGrid>
            </Stack>
        </EntityModal>
    );
}
